import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import NavBarWithoutLogo from '../../components/header/navaBarWithoutLogo';
import BreadCrumb from '../../components/breadCrumb/BreadCrumb';
import InputFieldPassword from '../../components/elements/inputFieldPassword';
import {
  useCreateUserAccount,
  useUpdateUserAccount,
} from '../../common/services/liferay/apiLiferayUser';
import Loading from '../../components/elements/loading';

interface ConfirmationPasswordProps {
  option: string;
}

const ConfirmationPassword: React.FC<ConfirmationPasswordProps> = ({ option }) => {
  const location = useLocation();
  const payload = location.state?.payload;
  const navigate = useNavigate();

  const { mutate, isSuccess, error, data, isPending } = useCreateUserAccount();
  const {
    mutate: update,
    isSuccess: isSuccessUpdate,
    error: errorUpdate,
    data: dataUpdate,
    isPending: isPendingUpdate,
  } = useUpdateUserAccount();

  const contrasenaInput = useRef<HTMLInputElement>(null);
  const confirmarContrasenaInput = useRef<HTMLInputElement>(null);
  const inputBorderContrasena = useRef<HTMLDivElement>(null);
  const inputBorderConfirmar = useRef<HTMLDivElement>(null);
  const checkNumero = useRef<HTMLInputElement>(null);
  const checkMinus = useRef<HTMLInputElement>(null);
  const checkMayus = useRef<HTMLInputElement>(null);
  const checkSeis = useRef<HTMLInputElement>(null);
  const checkDoce = useRef<HTMLInputElement>(null);
  const checkEspecial = useRef<HTMLInputElement>(null);
  const checkCoinciden = useRef<HTMLInputElement>(null);

  const updateCheckboxes = () => {
    const contrasena = contrasenaInput.current?.value || '';
    const confirmar = confirmarContrasenaInput.current?.value || '';

    if (checkNumero.current) checkNumero.current.checked = /\d/.test(contrasena);
    if (checkMinus.current) checkMinus.current.checked = /[a-z]/.test(contrasena);
    if (checkMayus.current) checkMayus.current.checked = /[A-Z]/.test(contrasena);
    if (checkSeis.current) checkSeis.current.checked = contrasena.length >= 12;
    if (checkDoce.current) checkDoce.current.checked = contrasena.length <= 15;
    if (checkEspecial.current) checkEspecial.current.checked = /[-+=|\\(){}[\]:;*.]/.test(contrasena);
    if (checkCoinciden.current) checkCoinciden.current.checked = contrasena === confirmar;

    updateIcons();

    if (inputBorderContrasena.current) {
      inputBorderContrasena.current.style.borderColor =
        checkNumero.current?.checked &&
        checkMinus.current?.checked &&
        checkMayus.current?.checked &&
        checkSeis.current?.checked &&
        checkDoce.current?.checked &&
        checkEspecial.current?.checked
          ? 'green'
          : 'red';
    }

    if (inputBorderConfirmar.current) {
      inputBorderConfirmar.current.style.borderColor = checkCoinciden.current?.checked
        ? 'green'
        : 'red';
    }
  };

  const updateIcons = () => {
    const iconMappings = [
      { id: 'icono-numero', check: checkNumero },
      { id: 'icono-minus', check: checkMinus },
      { id: 'icono-mayus', check: checkMayus },
      { id: 'icono-seis', check: checkSeis },
      { id: 'icono-doce', check: checkDoce },
      { id: 'icono-especial', check: checkEspecial },
      { id: 'icono-coinciden', check: checkCoinciden },
    ];

    iconMappings.forEach(({ id, check }) => {
      const icon = document.getElementById(id);
      if (icon) {
        const isChecked = check.current?.checked || false;
        icon.classList.toggle('w--redirected-checked', isChecked);
        icon.style.backgroundImage = isChecked ? "url('/documents/d/guest/Checkbox')" : '';
      }
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const allChecked = [
      checkNumero.current,
      checkMinus.current,
      checkMayus.current,
      checkSeis.current,
      checkDoce.current,
      checkEspecial.current,
      checkCoinciden.current,
    ].every((checkbox) => checkbox?.checked);

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const formJson = Object.fromEntries(formData.entries());

    if (!allChecked) {
      alert('Por favor, asegúrate de que todas las condiciones están cumplidas.');
      return;
    }

    const contrasena = formJson.contrasena as string;

    if (option === 'forgotPassword') {
      update({ payload, contrasena });
    } else if (option === 'register') {
      mutate({ newPassword: contrasena, payload });
    }
  };
  useEffect(() => {
    if (isSuccess) {
      navigate('/quiero-registrarme/confirmacion-de-registro', { state: { payload } });
    } else if (error) {
      navigate('/olvide-contrasena/error-de-actualizar-dni');
    }
  }, [isSuccess, data, error, navigate, payload]);

  useEffect(() => {
    if (isSuccessUpdate) {
      navigate('/olvide-contrasena/confirmacion-de-cambio-de-contrasena', { state: { payload } });
    } else if (errorUpdate) {
      navigate('/olvide-contrasena/error-de-actualizar-dni');
    }
  }, [isSuccessUpdate, dataUpdate, errorUpdate, navigate, payload]);

  return (
    <>
      <header className="menu is-blanco w-nav" data-collapse="medium" data-duration="400" data-easing="ease" data-easing2="ease">
        <NavBarWithoutLogo />
      </header>
      {(isPending || isPendingUpdate) && <Loading />}
      <section className="info-h">
        <div className="padding-global">
          <div className="padding-section-medium">
            <div className="container-medium">
              <div className="info_wrp">
                <div className="infor_form w-form">
                  <form onSubmit={handleSubmit} className="form-paso-1">
                    <BreadCrumb currentStep={3} />
                    <h2 className="text-align-center">Crea una nueva contraseña</h2>
                    <div className="form_bx is-70">
                      <div ref={inputBorderContrasena}>
                        <InputFieldPassword ref={contrasenaInput} name="contrasena" onChange={updateCheckboxes} />
                      </div>
                      <div ref={inputBorderConfirmar}>
                        <InputFieldPassword ref={confirmarContrasenaInput} name="confirmar" onChange={updateCheckboxes} />
                      </div>
                      <div className="form_text-alert">Contraseña inválida</div>
                    </div>

                    {/* Validaciones visuales */}
                    <div className="checks w-embed">
                      {[
                        { id: 'numero', label: 'Debe tener al menos un número', ref: checkNumero },
                        { id: 'minus', label: 'Debe tener al menos una minúscula', ref: checkMinus },
                        { id: 'mayus', label: 'Debe tener al menos una mayúscula', ref: checkMayus },
                        { id: 'seis', label: 'Debe tener mínimo 12 caracteres', ref: checkSeis },
                        { id: 'doce', label: 'Debe tener máximo 15 caracteres', ref: checkDoce },
                        { id: 'especial', label: String.raw`Debe tener al menos un carácter especial ( - + = | \ ( ) { } [ ] : ; * . )`, ref: checkEspecial },
                        { id: 'coinciden', label: 'Las contraseñas coinciden', ref: checkCoinciden },
                      ].map((check) => (
                        <label className="w-checkbox form_check" key={check.id}>
                          <div 
                            className="w-checkbox-input w-checkbox-input--inputType-custom check-round" 
                            id={`icono-${check.id}`}
                          />
                          <input
                            type="checkbox"
                            id={`check${check.id}`}
                            style={{ opacity: 0, position: 'absolute', zIndex: -1 }}
                            ref={check.ref}
                            disabled
                          />
                          <label className="w-form-label text-size-regular" htmlFor={`check${check.id}`}>
                            {check.label}
                          </label>
                        </label>
                      ))}
                    </div>

                    <div className="pasarella_btns">
                      <Link to="-1" className="button is-large w-button">
                        Regresar
                      </Link>
                      <button type="submit" className="button is-large outline w-button">
                        Continuar
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConfirmationPassword;
